import { IApiError } from '@frontend/request';

import copyTradeAPI from '@aqx-core/common/api/copy-trade/api';
import {
  IPelicanCopyTradeAuthParams,
  IPelicanCopyTradeBroker,
  IPelicanCopyTradeCloseCopyTradeOrderParams,
  IPelicanCopyTradeCopierInfo,
  IPelicanCopyTradeCopierInfoParams,
  IPelicanCopyTradeCopySettings,
  IPelicanCopyTradeCopySettingsParams,
  IPelicanCopyTradeCreateCopySettingsParams,
  IPelicanCopyTradeEditCopierDrawdownParams,
  IPelicanCopyTradeEditCopyTradeOrderParams,
  IPelicanCopyTradeLinkCopierParams,
  IPelicanCopyTradeMissedTrades,
  IPelicanCopyTradeOrderOpenPositionParams,
  IPelicanCopyTradeProfile,
  IPelicanCopyTradeProfileParams,
  IPelicanCopyTradeRemoveCopierParams,
  IPelicanCopyTradeResetCopierParams,
  IPelicanCopyTradeServer,
  IPelicanCopyTradeSignalClosedColumns,
  IPelicanCopyTradeSignalOpenColumns,
  IPelicanCopyTradeStopCopySettingsParams,
  IPelicanCopyTradeStrategyDetailData,
  IPelicanCopyTradeStrategyDetailParams,
  IPelicanCopyTradeStrategyListParams,
  IPelicanCopyTradeStrategyListResponse,
  IPelicanCopyTradeUserInfo,
  IPelicanEditSectionSettings,
  IPelicanSectionSettings
} from '@aqx-core/common/api/copy-trade/model';
import { IAPIControllerCallbacks } from '@aqx-core/common/api/types';
import { ECopyTradeFilter } from '@aqx-core/common/constants/copy-trade';
import { ECommonResponseCode } from '@aqx-core/common/request';

class CopyTradeAPIController {
  private sectionSwitchAC?: AbortController;
  private strategyDetailAC?: AbortController;
  private userInfoAC?: AbortController;

  public abortSectionSwitch = () => {
    this.sectionSwitchAC?.abort();
    this.sectionSwitchAC = undefined;
  };

  public createSectionSwitchSignal = (): AbortSignal => {
    this.sectionSwitchAC?.abort();
    this.sectionSwitchAC = new AbortController();
    return this.sectionSwitchAC.signal;
  };

  public getSectionSwitchSignal = (): AbortSignal | undefined => {
    return this.sectionSwitchAC?.signal;
  };

  public abortStrategyDetail = () => {
    this.strategyDetailAC?.abort();
    this.strategyDetailAC = undefined;
  };

  public createStrategyDetailSignal = (): AbortSignal => {
    this.strategyDetailAC?.abort();
    this.strategyDetailAC = new AbortController();
    return this.strategyDetailAC.signal;
  };

  public createUserInfoSignal = (): AbortSignal => {
    this.userInfoAC?.abort();
    this.userInfoAC = new AbortController();
    return this.userInfoAC.signal;
  };

  public auth = async (
    params: IPelicanCopyTradeAuthParams,
    callbacks: IAPIControllerCallbacks<ECommonResponseCode>
  ) => {
    try {
      const { code } = await copyTradeAPI.auth(params);
      callbacks.onSuccess(code);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getUserInfo = async (callbacks: IAPIControllerCallbacks<IPelicanCopyTradeUserInfo>, signal?: AbortSignal) => {
    try {
      const { result } = await copyTradeAPI.getUserInfo(signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getAvailableStrategySections = async (callbacks: IAPIControllerCallbacks<ECopyTradeFilter[]>) => {
    try {
      const { result } = await copyTradeAPI.getAvailableStrategySections();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getAllStrategySections = async (callbacks: IAPIControllerCallbacks<IPelicanSectionSettings[]>) => {
    try {
      const { result } = await copyTradeAPI.getAllStrategySections();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public editAvailableStrategySections = async (
    params: IPelicanEditSectionSettings,
    callbacks: IAPIControllerCallbacks<boolean>
  ) => {
    try {
      const { result } = await copyTradeAPI.editAvailableStrategySections(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getStrategyList = async (
    params: IPelicanCopyTradeStrategyListParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeStrategyListResponse>,
    signal?: AbortSignal
  ) => {
    try {
      const { result } = await copyTradeAPI.getStrategyList(params, signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getStrategyDetail = async (
    params: IPelicanCopyTradeStrategyDetailParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeStrategyDetailData>,
    signal?: AbortSignal
  ) => {
    try {
      const { result } = await copyTradeAPI.getStrategyDetail(params, signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getStrategyImage = async (
    params: IPelicanCopyTradeStrategyDetailParams,
    callbacks: IAPIControllerCallbacks<Blob | undefined>
  ) => {
    try {
      const blob = (await copyTradeAPI.getStrategyImage(params)) as unknown as Blob;
      callbacks.onSuccess(blob);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getCopyTradeOpenPositions = async (
    params: IPelicanCopyTradeStrategyDetailParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeSignalOpenColumns[]>
  ) => {
    try {
      const { result } = await copyTradeAPI.getCopyTradeOpenPositions(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getCopyTradeHistory = async (
    params: IPelicanCopyTradeStrategyDetailParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeSignalClosedColumns[]>
  ) => {
    try {
      const { result } = await copyTradeAPI.getCopyTradeHistory(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  // Master data controller
  public getBrokers = async (callbacks: IAPIControllerCallbacks<IPelicanCopyTradeBroker[]>) => {
    try {
      const { result } = await copyTradeAPI.getBrokers();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getServers = async (callbacks: IAPIControllerCallbacks<IPelicanCopyTradeServer[]>) => {
    try {
      const { result } = await copyTradeAPI.getServers();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  // Copier
  public linkCopier = async (
    params: IPelicanCopyTradeLinkCopierParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeCopierInfo>
  ) => {
    try {
      const { result } = await copyTradeAPI.linkCopier(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getCopierInfo = async (
    params: IPelicanCopyTradeCopierInfoParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeCopierInfo>
  ) => {
    try {
      const { result } = await copyTradeAPI.getCopierInfo(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public unlinkCopier = async (
    params: IPelicanCopyTradeRemoveCopierParams,
    callbacks: IAPIControllerCallbacks<boolean>
  ) => {
    try {
      const { result } = await copyTradeAPI.removeCopier(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public resetCopierDrawdown = async (
    params: IPelicanCopyTradeResetCopierParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeCopierInfo>
  ) => {
    try {
      const { result } = await copyTradeAPI.resetCopierDrawdown(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public editCopierDrawdown = async (
    params: IPelicanCopyTradeEditCopierDrawdownParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeCopierInfo>
  ) => {
    try {
      const { result } = await copyTradeAPI.editCopierDrawdown(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  // Copy settings
  public getCopySettings = async (
    params: IPelicanCopyTradeCopySettingsParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeCopySettings>
  ) => {
    try {
      const { result } = await copyTradeAPI.getCopySettings(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public editCopySettings = async (
    params: IPelicanCopyTradeCreateCopySettingsParams,
    callbacks: IAPIControllerCallbacks<boolean>
  ) => {
    try {
      const { result } = await copyTradeAPI.editCopySettings(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public createCopySettings = async (
    params: IPelicanCopyTradeCreateCopySettingsParams,
    callbacks: IAPIControllerCallbacks<boolean>
  ) => {
    try {
      const { result } = await copyTradeAPI.createCopySettings(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public stopCopySettings = async (
    params: IPelicanCopyTradeStopCopySettingsParams,
    callbacks: IAPIControllerCallbacks<boolean>
  ) => {
    try {
      const { result } = await copyTradeAPI.stopCopySettings(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  // assets
  public getCopyTradeProfile = async (
    params: IPelicanCopyTradeProfileParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeProfile>
  ) => {
    try {
      const { result } = await copyTradeAPI.getCopyTradeProfile(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getCopyTradeProfileImage = async (
    params: IPelicanCopyTradeProfileParams,
    callbacks: IAPIControllerCallbacks<Blob | undefined>
  ) => {
    try {
      const blob = (await copyTradeAPI.getCopyTradeProfileImage(params)) as unknown as Blob;
      callbacks.onSuccess(blob);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getCopierMissedTrades = async (
    params: IPelicanCopyTradeProfileParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeMissedTrades[]>
  ) => {
    try {
      const { result } = await copyTradeAPI.getCopierMissedTrades(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getCopyTradeOrder = async (
    params: IPelicanCopyTradeOrderOpenPositionParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeSignalOpenColumns>
  ) => {
    try {
      const { result } = await copyTradeAPI.getCopyTradeOrder(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public editCopyTradeOrder = async (
    params: IPelicanCopyTradeEditCopyTradeOrderParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeSignalClosedColumns>
  ) => {
    try {
      const { result } = await copyTradeAPI.editCopyTradeOrder(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public closeCopyTradeOrder = async (
    params: IPelicanCopyTradeCloseCopyTradeOrderParams,
    callbacks: IAPIControllerCallbacks<IPelicanCopyTradeSignalClosedColumns>
  ) => {
    try {
      const { result } = await copyTradeAPI.closeCopyTradeOrder(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public switchCopierAccount = async (copierId: string, callbacks: IAPIControllerCallbacks<string>) => {
    try {
      const { result } = await copyTradeAPI.switchCopierAccount(copierId);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };
}

export const copyTradeAPIController = new CopyTradeAPIController();
